import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save, Clock, CalendarCheck, Pencil, X, ChevronLeft, ChevronRight, Mail, Phone, RotateCcw, CheckCircle2, ChevronRight as ChevRight } from "lucide-react";
import { toast } from "sonner";
import {
  adminListTypes, adminCreateType, adminUpdateType, adminDeleteType,
  adminGetSchedulingConfig, adminSetSchedulingConfig, adminListBookings, adminSetBookingStatus,
  adminDeleteBooking, adminBookingAvailability, adminRescheduleBooking, adminRequestBookingPayment, formatApiError,
} from "@/lib/api";

const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const money = (v) => `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
const input = "w-full border border-steel-border bg-steel-elevated px-3 py-2 text-sm text-steel-text outline-none focus:border-steel-brass";
const card = "border border-steel-border bg-steel-surface p-6";

const emptyType = { name: "", description: "", duration_minutes: 30, deposit_amount: 50, active: true, sort: 0 };

const pad2 = (n) => String(n).padStart(2, "0");
const WEEKHEAD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function MonthCalendar({ bookings, timezone, onSelect }) {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });

  const byDate = {};
  bookings.forEach((b) => {
    try {
      const key = new Date(b.start).toLocaleDateString("en-CA", { timeZone: tz });
      (byDate[key] = byDate[key] || []).push(b);
    } catch { /* ignore */ }
  });
  Object.values(byDate).forEach((list) => list.sort((a, c) => a.start.localeCompare(c.start)));

  const startDay = new Date(cursor.y, cursor.m, 1).getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const todayKey = new Date().toLocaleDateString("en-CA", { timeZone: tz });
  const keyFor = (d) => `${cursor.y}-${pad2(cursor.m + 1)}-${pad2(d)}`;
  const shift = (delta) => setCursor((c) => {
    const d = new Date(c.y, c.m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" });

  return (
    <div data-testid="booking-calendar">
      <div className="flex items-center justify-between">
        <p className="font-display text-lg text-steel-text" data-testid="calendar-month-label">{monthLabel}</p>
        <div className="flex items-center gap-2">
          <button data-testid="calendar-today" onClick={() => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); }}
            className="border border-steel-border px-3 py-1.5 text-xs text-steel-secondary hover:text-steel-text">Today</button>
          <button data-testid="calendar-prev" onClick={() => shift(-1)} className="border border-steel-border p-1.5 text-steel-secondary hover:text-steel-text"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="calendar-next" onClick={() => shift(1)} className="border border-steel-border p-1.5 text-steel-secondary hover:text-steel-text"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="mt-4 overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-7 border-b border-steel-border">
            {WEEKHEAD.map((w) => (
              <div key={w} className="py-2 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-steel-muted">{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((d, i) => {
              const key = d ? keyFor(d) : null;
              const items = (key && byDate[key]) || [];
              const isToday = key === todayKey;
              return (
                <div key={i} data-testid={d ? `cal-day-${key}` : undefined}
                  className={`flex max-h-40 min-h-[104px] flex-col gap-1 overflow-y-auto border-b border-r border-steel-border p-1.5 ${i % 7 === 0 ? "border-l" : ""} ${d ? "" : "bg-steel-elevated/30"}`}>
                  {d && (
                    <span className={`text-right font-mono text-[11px] ${isToday ? "font-bold text-steel-brass" : "text-steel-muted"}`}>{d}</span>
                  )}
                  {items.map((b) => (
                    <button key={b.id} type="button" onClick={() => onSelect && onSelect(b)}
                      data-testid={`cal-booking-${b.id}`} title={`${b.name} · ${b.type_name}`}
                      className={`truncate border-l-2 px-1.5 py-1 text-left text-[11px] leading-tight transition-colors hover:brightness-125 ${b.booking_kind === "message" ? "border-sky-500 bg-sky-500/15 text-sky-300" : b.deposit_status === "paid" ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-amber-400 bg-amber-400/15 text-amber-300"}`}>
                      <span className="font-semibold">{fmtTime(b.start)}</span> {b.name}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function TypeForm({ initial, onSave, onCancel }) {
  const [t, setT] = useState(initial);
  return (
    <div className="mt-4 border border-steel-brass/40 bg-steel-elevated p-5" data-testid="consult-type-form">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input data-testid="ct-name" className={input} placeholder="Name (e.g. Initial Tax Consultation)" value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} />
        <input className={input} placeholder="Short description" value={t.description} onChange={(e) => setT({ ...t, description: e.target.value })} />
        <label className="text-xs text-steel-muted">Duration (minutes)
          <input data-testid="ct-duration" type="number" min="10" className={`${input} mt-1`} value={t.duration_minutes} onChange={(e) => setT({ ...t, duration_minutes: Number(e.target.value) })} /></label>
        <label className="text-xs text-steel-muted">Deposit amount ($)
          <input data-testid="ct-deposit" type="number" min="0" step="1" className={`${input} mt-1`} value={t.deposit_amount} onChange={(e) => setT({ ...t, deposit_amount: Number(e.target.value) })} /></label>
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm text-steel-secondary">
        <input type="checkbox" checked={t.active} onChange={(e) => setT({ ...t, active: e.target.checked })} className="h-4 w-4 accent-purple-500" /> Open for booking
      </label>
      <div className="mt-4 flex gap-3">
        <button data-testid="ct-save" onClick={() => onSave(t)} className="flex items-center gap-2 bg-steel-accent px-5 py-2.5 text-sm font-semibold text-white hover:bg-steel-accentHover"><Save className="h-4 w-4" /> Save</button>
        <button onClick={onCancel} className="border border-steel-border px-5 py-2.5 text-sm text-steel-secondary hover:text-steel-text">Cancel</button>
      </div>
    </div>
  );
}

const STATUS_LABELS = { confirmed: "Confirmed", completed: "Completed", no_show: "No-show", cancelled: "Cancelled", pending_payment: "Awaiting deposit" };

function BookingModal({ booking, adminKey, timezone, onClose, onChanged }) {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isMessage = booking.booking_kind === "message";
  const paid = booking.deposit_status === "paid";
  const [busy, setBusy] = useState(false);
  const [resched, setResched] = useState(false);
  const [day, setDay] = useState(new Date(booking.start).toLocaleDateString("en-CA", { timeZone: tz }));
  const [slots, setSlots] = useState(null);
  const [picked, setPicked] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    if (!resched) return;
    setSlots(null); setPicked(null);
    adminBookingAvailability(adminKey, booking.id, day)
      .then((d) => setSlots(d.slots)).catch(() => setSlots([]));
  }, [resched, day]);

  const fmt = (iso, opts) => new Date(iso).toLocaleString("en-US", { timeZone: tz, ...opts });
  const accent = isMessage ? "text-sky-300" : paid ? "text-emerald-300" : "text-amber-300";
  const ring = isMessage ? "border-sky-500/50" : paid ? "border-emerald-500/50" : "border-amber-400/50";

  const run = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); if (msg) toast.success(msg); return r; }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const setStatus = async (status) => { await run(() => adminSetBookingStatus(adminKey, booking.id, status), "Status updated."); onChanged(); };
  const doReschedule = async () => {
    if (!picked) return;
    const r = await run(() => adminRescheduleBooking(adminKey, booking.id, picked), "Consultation rescheduled — client emailed.");
    if (r) { onChanged(); setResched(false); }
  };
  const doDelete = async () => {
    const r = await run(() => adminDeleteBooking(adminKey, booking.id), "Booking deleted.");
    if (r) { onChanged(); onClose(); }
  };
  const requestPayment = async () => {
    const r = await run(() => adminRequestBookingPayment(adminKey, booking.id), "Payment request emailed to the client.");
    if (r) onChanged();
  };
  const needsDeposit = !paid && Number(booking.deposit_amount) > 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" data-testid="booking-modal" onClick={onClose}>
      <div className={`mt-10 w-full max-w-lg border ${ring} bg-steel-surface shadow-2xl`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 border-b border-steel-border p-5">
          <div className="min-w-0">
            <p className={`font-mono text-[10px] uppercase tracking-[0.2em] ${accent}`}>{isMessage ? "Website message" : paid ? `Deposit paid · $${Number(booking.deposit_amount).toFixed(2)}` : "No deposit on file"}</p>
            <h3 className={`mt-1 font-display text-2xl font-medium ${isMessage ? "text-sky-200" : paid ? "text-emerald-200" : "text-steel-text"}`} data-testid="modal-booking-name">{booking.name}</h3>
            <p className="text-sm text-steel-secondary">{isMessage ? "Quick message" : `${booking.type_name} · ${booking.duration_minutes} min`}</p>
          </div>
          <button data-testid="modal-close" onClick={onClose} className="text-steel-muted hover:text-steel-text"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4 p-5">
          <div className="flex items-center gap-2 text-sm text-steel-text">
            <CalendarCheck className="h-4 w-4 text-steel-brass" />
            {fmt(booking.start, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-steel-secondary">
            <a href={`mailto:${booking.email}`} className="flex items-center gap-1.5 hover:text-steel-brass"><Mail className="h-4 w-4" /> {booking.email}</a>
            {booking.phone && <a href={`tel:${booking.phone}`} className="flex items-center gap-1.5 hover:text-steel-brass"><Phone className="h-4 w-4" /> {booking.phone}</a>}
          </div>
          {booking.notes && <p className="border-l-2 border-steel-border pl-3 text-sm italic text-steel-secondary">“{booking.notes}”</p>}

          {/* status */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Status</label>
            <select data-testid="modal-status" value={booking.status} onChange={(e) => setStatus(e.target.value)} disabled={busy}
              className="mt-1 w-full border border-steel-border bg-steel-elevated px-3 py-2 text-sm text-steel-text outline-none focus:border-steel-brass">
              {["confirmed", "completed", "no_show", "cancelled"].map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </div>

          {/* email payment request (unpaid deposits) */}
          {needsDeposit && (
            <div data-testid="modal-request-payment-wrap">
              <button data-testid="modal-request-payment" onClick={requestPayment} disabled={busy}
                className="flex w-full items-center justify-center gap-2 border border-steel-brass/50 px-4 py-2.5 text-sm font-semibold text-steel-brass hover:bg-steel-brass/10 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                {booking.pay_request_sent_at ? "Resend deposit payment request" : "Email deposit payment request"}
              </button>
              {booking.pay_request_sent_at && (
                <p className="mt-1 text-xs text-steel-muted">Payment request emailed {new Date(booking.pay_request_sent_at).toLocaleString("en-US")}.</p>
              )}
            </div>
          )}

          {/* reschedule */}
          {!isMessage && (!resched ? (
            <button data-testid="modal-reschedule-open" onClick={() => setResched(true)} disabled={busy}
              className="flex w-full items-center justify-center gap-2 border border-steel-border px-4 py-2.5 text-sm font-semibold text-steel-text hover:border-steel-brass">
              <RotateCcw className="h-4 w-4" /> Reschedule
            </button>
          ) : (
            <div className="border border-steel-brass/40 bg-steel-elevated p-4" data-testid="modal-reschedule-panel">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-steel-text">Pick a new time</p>
                <button onClick={() => setResched(false)} className="text-steel-muted hover:text-steel-text"><X className="h-4 w-4" /></button>
              </div>
              <input type="date" data-testid="modal-reschedule-date" value={day} min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setDay(e.target.value)} className="mt-3 border border-steel-border bg-steel-surface px-3 py-2 text-sm text-steel-text [color-scheme:dark]" />
              <div className="mt-3 flex min-h-[40px] flex-wrap gap-2">
                {slots === null ? <Loader2 className="h-4 w-4 animate-spin text-steel-brass" />
                  : slots.length === 0 ? <span className="text-xs text-steel-muted">No open times on this day.</span>
                  : slots.map((s) => (
                    <button key={s} data-testid={`modal-slot-${s}`} onClick={() => setPicked(s)}
                      className={`border px-3 py-1.5 text-xs ${picked === s ? "border-steel-brass bg-steel-brass/10 text-steel-brass" : "border-steel-border text-steel-secondary hover:text-steel-text"}`}>
                      {new Date(s).toLocaleTimeString("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" })}
                    </button>
                  ))}
              </div>
              <button data-testid="modal-reschedule-confirm" onClick={doReschedule} disabled={busy || !picked}
                className="mt-4 flex items-center gap-2 bg-steel-accent px-5 py-2.5 text-sm font-semibold text-white hover:bg-steel-accentHover disabled:opacity-40">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirm new time
              </button>
            </div>
          ))}

          {/* delete / cancel */}
          {paid ? (
            <p className="text-xs text-steel-muted">Deposit was paid, so this booking can't be deleted. Set status to <strong>Cancelled</strong> above — the client keeps their ${Number(booking.deposit_amount).toFixed(2)} as account credit.</p>
          ) : !confirmDel ? (
            <button data-testid="modal-delete-open" onClick={() => setConfirmDel(true)} disabled={busy}
              className="flex w-full items-center justify-center gap-2 border border-red-500/40 px-4 py-2.5 text-sm font-semibold text-red-400 hover:bg-red-500/10">
              <Trash2 className="h-4 w-4" /> Delete booking
            </button>
          ) : (
            <div className="flex items-center gap-3 border border-red-500/40 bg-red-500/5 p-3">
              <span className="text-sm text-steel-secondary">Delete this unpaid booking permanently?</span>
              <button data-testid="modal-delete-confirm" onClick={doDelete} disabled={busy} className="ml-auto bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-400 disabled:opacity-40">Delete</button>
              <button onClick={() => setConfirmDel(false)} className="text-sm text-steel-muted hover:text-steel-text">Keep</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SchedulingTab({ adminKey }) {
  const [types, setTypes] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [editing, setEditing] = useState(null); // type being edited, or "new"
  const [savingCfg, setSavingCfg] = useState(false);
  const [selected, setSelected] = useState(null); // booking open in modal

  const load = () => {
    adminListTypes(adminKey).then(setTypes).catch(() => setTypes([]));
    adminGetSchedulingConfig(adminKey).then(setCfg).catch(() => {});
    return adminListBookings(adminKey).then((bk) => {
      setBookings(bk);
      setSelected((cur) => (cur ? bk.find((x) => x.id === cur.id) || null : null));
      return bk;
    }).catch(() => setBookings([]));
  };
  useEffect(() => { load(); }, []);

  const saveType = async (t) => {
    try {
      if (t.id) await adminUpdateType(adminKey, t.id, t);
      else await adminCreateType(adminKey, t);
      toast.success("Consultation type saved.");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeType = async (id) => {
    try { await adminDeleteType(adminKey, id); toast.success("Removed from booking."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveCfg = async () => {
    setSavingCfg(true);
    try { const c = await adminSetSchedulingConfig(adminKey, cfg); setCfg(c); toast.success("Availability saved."); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingCfg(false); }
  };
  const setWindow = (dayKey, idx, field, val) => {
    const weekly = { ...cfg.weekly };
    const list = [...(weekly[dayKey] || [])];
    list[idx] = { ...list[idx], [field]: val };
    weekly[dayKey] = list;
    setCfg({ ...cfg, weekly });
  };
  const addWindow = (dayKey) => {
    const weekly = { ...cfg.weekly };
    weekly[dayKey] = [...(weekly[dayKey] || []), { start: "09:00", end: "17:00" }];
    setCfg({ ...cfg, weekly });
  };
  const removeWindow = (dayKey, idx) => {
    const weekly = { ...cfg.weekly };
    weekly[dayKey] = (weekly[dayKey] || []).filter((_, i) => i !== idx);
    setCfg({ ...cfg, weekly });
  };

  return (
    <div className="space-y-8" data-testid="scheduling-tab">
      {/* CONSULTATION TYPES */}
      <section className={card}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-medium text-steel-text">Consultation types & deposits</h3>
          <button data-testid="add-consult-type-btn" onClick={() => setEditing({ ...emptyType })} className="flex items-center gap-2 border border-steel-brass/50 px-4 py-2 text-sm font-semibold text-steel-brass hover:bg-steel-brass/10"><Plus className="h-4 w-4" /> Add type</button>
        </div>
        <p className="mt-2 text-xs text-steel-muted">Set your own consultation offerings and the non-refundable deposit for each. Deposits become client account credit.</p>
        <div className="mt-5 space-y-3">
          {types.length === 0 && <p className="text-sm text-steel-secondary">No consultation types yet. Add one to start taking bookings.</p>}
          {types.map((t) => (
            <div key={t.id} data-testid={`consult-type-${t.id}`} className="flex flex-wrap items-center justify-between gap-3 border border-steel-border bg-steel-elevated px-4 py-3">
              <div className="min-w-0">
                <p className="font-medium text-steel-text">{t.name} {!t.active && <span className="ml-2 font-mono text-[10px] uppercase text-steel-muted">(hidden)</span>}</p>
                <p className="text-xs text-steel-muted"><Clock className="mr-1 inline h-3 w-3" />{t.duration_minutes} min · {t.deposit_amount > 0 ? `${money(t.deposit_amount)} deposit` : "Free"}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(t)} className="border border-steel-border p-2 text-steel-secondary hover:text-steel-text"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`del-type-${t.id}`} onClick={() => removeType(t.id)} className="border border-steel-border p-2 text-steel-secondary hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
        {editing && <TypeForm initial={editing} onSave={saveType} onCancel={() => setEditing(null)} />}
      </section>

      {/* AVAILABILITY */}
      {cfg && (
        <section className={card} data-testid="availability-config">
          <h3 className="font-display text-xl font-medium text-steel-text">Weekly availability</h3>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <label className="text-xs text-steel-muted">Slot length (min)<input data-testid="cfg-slot" type="number" min="10" className={`${input} mt-1`} value={cfg.slot_length_minutes} onChange={(e) => setCfg({ ...cfg, slot_length_minutes: Number(e.target.value) })} /></label>
            <label className="text-xs text-steel-muted">Buffer (min)<input type="number" min="0" className={`${input} mt-1`} value={cfg.buffer_minutes} onChange={(e) => setCfg({ ...cfg, buffer_minutes: Number(e.target.value) })} /></label>
            <label className="text-xs text-steel-muted">Min lead (hrs)<input type="number" min="0" className={`${input} mt-1`} value={cfg.min_lead_hours} onChange={(e) => setCfg({ ...cfg, min_lead_hours: Number(e.target.value) })} /></label>
            <label className="text-xs text-steel-muted">Book up to (days)<input type="number" min="1" className={`${input} mt-1`} value={cfg.max_days_ahead} onChange={(e) => setCfg({ ...cfg, max_days_ahead: Number(e.target.value) })} /></label>
          </div>
          <label className="mt-3 block text-xs text-steel-muted">Timezone<input className={`${input} mt-1 max-w-xs`} value={cfg.timezone} onChange={(e) => setCfg({ ...cfg, timezone: e.target.value })} /></label>
          <div className="mt-5 space-y-2">
            {DAYS.map(([key, label]) => (
              <div key={key} className="flex flex-wrap items-center gap-3 border-b border-dashed border-steel-border py-2">
                <span className="w-10 font-mono text-xs uppercase text-steel-brass">{label}</span>
                {(cfg.weekly[key] || []).map((w, i) => (
                  <span key={i} className="flex items-center gap-1">
                    <input type="time" value={w.start} onChange={(e) => setWindow(key, i, "start", e.target.value)} className="border border-steel-border bg-steel-elevated px-2 py-1 text-xs text-steel-text [color-scheme:dark]" />
                    <span className="text-steel-muted">–</span>
                    <input type="time" value={w.end} onChange={(e) => setWindow(key, i, "end", e.target.value)} className="border border-steel-border bg-steel-elevated px-2 py-1 text-xs text-steel-text [color-scheme:dark]" />
                    <button onClick={() => removeWindow(key, i)} className="text-steel-muted hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
                  </span>
                ))}
                <button onClick={() => addWindow(key)} className="text-xs text-steel-brass hover:underline">+ hours</button>
              </div>
            ))}
          </div>
          <button data-testid="save-config-btn" onClick={saveCfg} disabled={savingCfg} className="mt-5 flex items-center gap-2 bg-steel-accent px-5 py-2.5 text-sm font-semibold text-white hover:bg-steel-accentHover disabled:opacity-50">
            {savingCfg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save availability
          </button>
        </section>
      )}

      {/* BOOKINGS */}
      <section className={card}>
        <h3 className="flex items-center gap-2 font-display text-xl font-medium text-steel-text"><CalendarCheck className="h-5 w-5 text-steel-brass" /> Bookings</h3>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px] text-steel-muted">
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 border-l-2 border-emerald-500 bg-emerald-500/20" /> Deposit paid</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 border-l-2 border-amber-400 bg-amber-400/20" /> Unpaid</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 border-l-2 border-sky-500 bg-sky-500/20" /> Message</span>
          <span>· Click any appointment to manage it</span>
        </div>
        <div className="mt-5">
          <MonthCalendar bookings={bookings} timezone={cfg?.timezone} onSelect={setSelected} />
        </div>
        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">All bookings</p>
        <div className="mt-3 space-y-3">
          {bookings.length === 0 && <p className="text-sm text-steel-secondary" data-testid="no-bookings">No bookings yet.</p>}
          {bookings.map((b) => (
            <button type="button" key={b.id} data-testid={`booking-${b.id}`} onClick={() => setSelected(b)}
              className={`flex w-full flex-wrap items-center justify-between gap-3 border-l-4 border border-steel-border bg-steel-elevated px-4 py-3 text-left transition-colors hover:border-steel-brass/50 ${b.booking_kind === "message" ? "border-l-sky-500" : b.deposit_status === "paid" ? "border-l-emerald-500" : "border-l-amber-400"}`}>
              <div className="min-w-0">
                <p className={`font-medium ${b.booking_kind === "message" ? "text-sky-200" : b.deposit_status === "paid" ? "text-emerald-200" : "text-steel-text"}`}>{b.name} · {b.type_name}</p>
                <p className="text-xs text-steel-muted">{new Date(b.start).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · {b.email}{b.phone ? ` · ${b.phone}` : ""}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`font-mono text-[10px] uppercase tracking-[0.15em] ${b.booking_kind === "message" ? "text-sky-400" : b.deposit_status === "paid" ? "text-emerald-400" : "text-amber-400"}`}>{b.booking_kind === "message" ? "message" : b.deposit_status === "paid" ? `deposit ${money(b.deposit_amount)}` : "unpaid"}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-steel-muted">{STATUS_LABELS[b.status] || b.status}</span>
                <ChevRight className="h-4 w-4 text-steel-muted" />
              </div>
            </button>
          ))}
        </div>
      </section>

      {selected && (
        <BookingModal key={selected.id} booking={selected} adminKey={adminKey} timezone={cfg?.timezone}
          onClose={() => setSelected(null)} onChanged={load} />
      )}
    </div>
  );
}
